import React, { useState, useEffect, useRef } from "react";
import CustomerSearch from './CustomerSearch';
import ProductSearch from './ProductSearch';
import {
  Plus, 
  X, 
  Calendar, 
  Package, 
  FileText, 
  AlertCircle, 
  Wrench, 
  Search, 
  DollarSign, 
  Info, 
  Clock, 
  ChevronDown,
  ChevronUp,
  User, 
  Phone, 
  Mail, 
  MapPin, 
  Hash, 
  Tag, 
  UserCheck, 
  Printer, 
  MessageSquare, 
  Trash2, 
  History,
  Save,
  UseRef,
} from "lucide-react";
import { ComponentProps, Case } from "../types/types";
import { supabaseService } from "../SupabaseService";
import { 
  ToastContainer, 
  useNotifications,
  ServiceLabelDialog,
  BestillingLabelDialog,
  SmsReminderDialog,
  ConfirmDialog 
} from './NotificationSystem'; 

interface NyRegistreringProps extends ComponentProps {
  editingCase: Case | null;
  clearEditingCase: () => void;
  hasUnsavedChanges?: boolean;
  setHasUnsavedChanges?: (value: boolean) => void;
  originalCategory?: string | null;
  showSuccess?: (message: string, title?: string) => void;
  showError?: (message: string, title?: string) => void;
  showWarning?: (message: string, title?: string) => void;
  showInfo?: (message: string, title?: string) => void;
  deletedCases?: Case[];         
  setDeletedCases?: (cases: Case[]) => void;
}

interface AmendoProduct {
  varenummer: string;
  navn: string;
  beskrivelse: string;
  enhet: string;
  priseksmva: number;
  prisinklmva: number;
  leverandør: string;
  underkategori: string;
}

const formatPhoneNumber = (phone: string) => {
  if (!phone || phone.length !== 8) return phone;
  return `${phone.slice(0, 3)} ${phone.slice(3, 5)} ${phone.slice(5, 8)}`;
};
const NyRegistrering: React.FC<NyRegistreringProps> = ({
  cases, 
  setCases, 
  customers, 
  setCustomers, 
  openCaseForEditing, 
  editingCase, 
  clearEditingCase, 
  hasUnsavedChanges, 
  setHasUnsavedChanges, 
  originalCategory, 
  showSuccess, 
  showError, 
  showWarning, 
  showInfo,
  deletedCases,
  setDeletedCases,
}) => {
  // Initial form data
  const initialFormData: Case = {
    id: 0, 
    kunde: "", 
    dato: new Date().toISOString().split("T")[0], 
    avd: "", 
    adresse: "", 
    postnrSted: "", 
    orgnr: "", 
    ref: "", 
    merking: "", 
    mail: "", 
    tlf: "", 
    maskinMerke: "", 
    serienr: "", 
    arsmodel: "", 
    beskrivelse: "",
    varelinjer: [{
      dato: new Date().toISOString().split("T")[0], 
      antall: "", 
      varenr: "", 
      varebeskrivelse: "", 
      eksklMva: "", 
      inklMva: "", 
      rabatt: "", 
      sum: "",
      levert: false
    }],
    delebestillingDato: "", 
    kommentar: "", 
    utfortDato: "", 
    serviceRepUtført: "", 
    utlevertAv: "", 
    betalt: false, 
    faktura: false, 
    betalingsmetode: null, 
    reklamasjon: false, 
    kategori: "", 
    opprettet: "",
    isVarekjop: false,
  };

  const [formData, setFormData] = useState<Case>(initialFormData);
  const lastSaveTime = useRef<number>(0);
const MIN_TIME_BETWEEN_SAVES = 2000; // 2 sekunder mellom lagringer

  // Dialog states
  const [showBestillingLabelDialog, setShowBestillingLabelDialog] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showSmsReminder, setShowSmsReminder] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [showServiceLabelDialog, setShowServiceLabelDialog] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Search and dropdown states
  const [searchTerm, setSearchTerm] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [filteredCustomers, setFilteredCustomers] = useState<any[]>([]);
  const [selectedCustomerIndex, setSelectedCustomerIndex] = useState(-1);
  const [showServiceRepDropdown, setShowServiceRepDropdown] = useState(false);
  const [showUtlevertAvDropdown, setShowUtlevertAvDropdown] = useState(false);

  // Validation and error states
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [phoneError, setPhoneError] = useState("");

  // Product states
  const [amendoProducts, setAmendoProducts] = useState<AmendoProduct[]>([]);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [productsLoading, setProductsLoading] = useState(true);

  // Other states
  const [smsCustomerInfo, setSmsCustomerInfo] = useState<{ 
    name: string; 
    phone: string 
  } | null>(null);
  const [caseHistory, setCaseHistory] = useState<any[]>([]);
  const [isVarekjopMode, setIsVarekjopMode] = useState(false);

  // Refs
  const customerSearchRef = useRef<HTMLDivElement>(null);
  const serviceRepRef = useRef<HTMLDivElement>(null);
  const utlevertAvRef = useRef<HTMLDivElement>(null);

  // Constants
  const serviceRepPersons = [
    "Robert", 
    "Tormod", 
    "Trond Øyvind", 
    "Johan", 
    "Odd Arthur"
  ];
  
  const utlevertAvPersons = [
    "Johan", 
    "Rose", 
    "Odd Arthur", 
    "Tormod", 
    "Trond Øyvind"
  ];

  const categories = [
    { 
      id: "Til bestilling", 
      name: "Til bestilling", 
      icon: Calendar, 
      color: "bg-yellow-500" 
    },
    { 
      id: "Bestilt", 
      name: "Bestilt", 
      icon: Package, 
      color: "bg-blue-500" 
    },
    { 
      id: "Service", 
      name: "Service", 
      icon: Wrench, 
      color: "bg-green-500" 
    },
    { 
      id: "Klar til henting", 
      name: "Klar til henting", 
      icon: Package, 
      color: "bg-orange-500" 
    },
    { 
      id: "Klar til fakturering", 
      name: "Klar til fakturering", 
      icon: FileText, 
      color: "bg-purple-500" 
    },
    { 
      id: "Reklamasjon", 
      name: "Reklamasjon", 
      icon: AlertCircle, 
      color: "bg-red-500" 
    },
    { 
      id: "Diverse/Vent", 
      name: "Diverse/Vent", 
      icon: Calendar, 
      color: "bg-gray-500" 
    },
    { 
      id: "Arkiv", 
      name: "Arkiv", 
      icon: FileText, 
      color: "bg-gray-600" 
    },
  ];

// Ny funksjon for å skrive ut bestilling-merkelapp
const printBestillingLabel = (caseData: Case) => {
  const printWindow = window.open('', '_blank', 'width=400,height=400');
  if (!printWindow) return;
  
  const labelHTML = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Hentelapp - ${caseData.kunde}</title>
        <style>
          @page { 
            size: 10cm 10cm; 
            margin: 0; 
          } 
          body { 
            font-family: Arial, sans-serif; 
            margin: 0; 
            padding: 15px; 
            box-sizing: border-box; 
            background: white; 
          } 
          .container { 
            height: calc(100vh - 30px); 
            display: flex; 
            flex-direction: column; 
            justify-content: space-between; 
          } 
          .field { 
            margin-bottom: 20px; 
          } 
          .label { 
            font-size: 11px; 
            color: #666; 
            margin-bottom: 3px; 
            text-transform: uppercase; 
            letter-spacing: 1px; 
          } 
          .value { 
            font-size: 18px; 
            font-weight: bold; 
            color: #000; 
            text-transform: uppercase; 
            word-wrap: break-word; 
          } 
          .phone { 
            font-size: 16px; 
            color: #333; 
          } 
          .items { 
            font-size: 14px; 
            color: #333; 
            text-transform: none; 
            line-height: 1.4; 
          } 
          .header { 
            text-align: center; 
            border-bottom: 2px solid #f97316; 
            padding-bottom: 10px; 
            margin-bottom: 20px; 
          } 
          .header h1 { 
            font-size: 16px; 
            color: #f97316; 
            margin: 0; 
            text-transform: uppercase; 
            letter-spacing: 2px; 
          }
          .footer { 
            text-align: center; 
            font-size: 10px; 
            color: #999; 
            border-top: 1px solid #eee; 
            padding-top: 10px; 
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div>
            <div class="header">
              <h1>KLAR TIL HENTING</h1>
            </div>
            
            <div class="field">
              <div class="label">Kunde</div>
              <div class="value">${caseData.kunde}</div>
            </div>
            
            ${caseData.tlf ? `
            <div class="field">
              <div class="label">Telefon</div>
              <div class="value phone">${caseData.tlf}</div>
            </div>
            ` : ''}
            
            <div class="field">
              <div class="label">Bestilt</div>
              <div class="value items">${caseData.beskrivelse}</div>
            </div>
            
            ${caseData.kommentar ? `
            <div class="field">
              <div class="label">Kommentar</div>
              <div class="value items">${caseData.kommentar}</div>
            </div>
            ` : ''}
            
            <div class="field">
              <div class="label">Ordre nr</div>
              <div class="value">#${caseData.id}</div>
            </div>
          </div>
          
          <div class="footer">
            Hentet: ${new Date().toLocaleDateString("nb-NO")}
          </div>
        </div>
      </body>
    </html>
  `;
  
  printWindow.document.open();
  printWindow.document.write(labelHTML);
  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
};

  const printServiceLabel = (caseData: Case) => {
    const printWindow = window.open('', '_blank', 'width=400,height=400');
    if (!printWindow) return;
    
    const labelHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Merkelapp - ${caseData.kunde}</title>
          <style>
            @page { 
              size: 10cm 10cm; 
              margin: 0; 
            } 
            body { 
              font-family: Arial, sans-serif; 
              margin: 0; 
              padding: 15px; 
              box-sizing: border-box; 
              background: white; 
            } 
            .container { 
              height: calc(100vh - 30px); 
              display: flex; 
              flex-direction: column; 
              justify-content: space-between; 
            } 
            .field { 
              margin-bottom: 20px; 
            } 
            .label { 
              font-size: 11px; 
              color: #666; 
              margin-bottom: 3px; 
              text-transform: uppercase; 
              letter-spacing: 1px; 
            } 
            .value { 
              font-size: 18px; 
              font-weight: bold; 
              color: #000; 
              text-transform: uppercase; 
              line-height: 1.2; 
            } 
            .phone { 
              font-size: 28px; 
              font-weight: bold; 
              color: #000; 
            } 
            .machine { 
              font-size: 28px; 
              font-weight: bold; 
              color: #000; 
              text-transform: uppercase; 
              margin-top: 15px; 
              padding-top: 15px; 
              border-top: 2px solid #ccc; 
            } 
            .serial { 
              font-size: 14px; 
              color: #666; 
              margin-top: 5px; 
              font-weight: normal; 
            } 
            .description { 
              font-size: 22px; 
              color: #000; 
              line-height: 1.3; 
              margin-top: 15px; 
              text-transform: none; 
            } 
            .comment { 
              font-size: 14px; 
              color: #666; 
              margin-top: 10px; 
              font-style: italic; 
            } 
            .footer { 
              font-size: 16px; 
              color: #666; 
              padding-top: 15px; 
              border-top: 1px solid #ccc; 
              margin-top: auto; 
            } 
            @media print { 
              body { 
                padding: 12px; 
              } 
            } 
          </style>
        </head>
        <body>
          <div class="container">
            <div>
              <div class="field">
                <div class="label">KUNDE</div>
                <div class="value">${(caseData.kunde || '').toUpperCase()}</div>
              </div>
              <div class="field">
                <div class="phone">📞 ${formatPhoneNumber(caseData.tlf || '')}</div>
              </div>
              <div class="machine">
                🔧 ${(caseData.maskinMerke || 'IKKE SPESIFISERT').toUpperCase()}
                ${caseData.serienr ? `<div class="serial">SN: ${caseData.serienr}</div>` : ''}
              </div>
              <div class="description">
                <div class="label">BESKRIVELSE</div>
                ${caseData.beskrivelse || 'Ingen beskrivelse oppgitt'}
              </div>
              ${caseData.kommentar ? `<div class="comment">💬 ${caseData.kommentar}</div>` : ''}
            </div>
            <div class="footer">
              Mottatt: ${new Date(caseData.dato).toLocaleDateString('no-NO')}
            </div>
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.write(labelHTML);
    printWindow.document.close();
    
    setTimeout(() => { 
      printWindow.print(); 
      setTimeout(() => printWindow.close(), 1000); 
    }, 250);
  };
  
  // useEffect for editing case
useEffect(() => {
  if (editingCase) {
    setFormData(editingCase);
    setIsVarekjopMode(editingCase.isVarekjop || false);
    if (setHasUnsavedChanges) setHasUnsavedChanges(false);
  } else {
    // Når editingCase blir null, tilbakestill skjemaet
    setFormData(initialFormData);
    if (setHasUnsavedChanges) setHasUnsavedChanges(false);
  }
}, [editingCase, setHasUnsavedChanges]);  
  // useEffect for loading products
  useEffect(() => {
    const loadProducts = async () => {
      setProductsLoading(true);
      try {
        const products = await supabaseService.loadProducts();
        if (products && products.length > 0) {
          setAmendoProducts(products);
          setProductsLoaded(true);
        }
      } catch (error) {
        console.warn("Kunne ikke laste produkter:", error);
      } finally {
        setProductsLoading(false);
      }
    };
    loadProducts();
  }, []);

  // useEffect for customer search
  useEffect(() => {
    if (searchTerm.length > 0) {
      const filtered = customers.filter(customer => {
        const search = searchTerm.toLowerCase();
        return (
          customer.name?.toLowerCase().includes(search) || 
          customer.tlf?.includes(search) || 
          customer.orgnr?.includes(search)
        );
      });
      setFilteredCustomers(filtered.slice(0, 10));
      setShowCustomerDropdown(filtered.length > 0);
    } else {
      setFilteredCustomers([]);
      setShowCustomerDropdown(false);
    }
  }, [searchTerm, customers]);

  // useEffect for click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (customerSearchRef.current && 
          !customerSearchRef.current.contains(event.target as Node)) {
        setShowCustomerDropdown(false);
      }
      if (serviceRepRef.current && 
          !serviceRepRef.current.contains(event.target as Node)) {
        setShowServiceRepDropdown(false);
      }
      if (utlevertAvRef.current && 
          !utlevertAvRef.current.contains(event.target as Node)) {
        setShowUtlevertAvDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Input change handler
  const handleInputChange = (field: keyof Case, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (validationErrors.length > 0) setValidationErrors([]);
    if (setHasUnsavedChanges) setHasUnsavedChanges(true);
  };
  
    const handlePhoneChange = (value: string) => {
      // Fjern alle mellomrom først
      const cleanValue = value.replace(/\s/g, '');
      
      // Tillat kun tall og maks 8 siffer
      if (/^\d{0,8}$/.test(cleanValue)) {
        handleInputChange("tlf", cleanValue);
        setPhoneError(cleanValue.length > 0 && cleanValue.length !== 8 ? "Telefonnummer må være 8 siffer" : "");
      }
    };

  // Phone validation
  const validatePhone = (phone: string): boolean => {
    const cleanPhone = phone.replace(/[\s-]/g, "");
    
    if (cleanPhone.length === 0) { 
      setPhoneError("Telefonnummer er påkrevd"); 
      return false; 
    }
    if (cleanPhone.length !== 8) { 
      setPhoneError("Telefonnummer må være 8 siffer"); 
      return false; 
    }
    if (!/^\d{8}$/.test(cleanPhone)) { 
      setPhoneError("Telefonnummer kan kun inneholde tall"); 
      return false; 
    }
    
    setPhoneError("");
    return true;
  };

  // Form validation
  const validateForm = (): boolean => {
  const errors: string[] = [];
  
  if (!formData.kunde.trim()) {
    errors.push("Kunde må fylles ut");
  }
  
  // Varekjøp-modus: Hopp over validering av tlf, maskin og beskrivelse
  if (!isVarekjopMode) {
    if (!formData.tlf.trim()) {
      errors.push("Telefonnummer må fylles ut");
    } else if (!validatePhone(formData.tlf)) {
      errors.push("Telefonnummer må være 8 siffer");
    }
    if (!formData.maskinMerke.trim()) {
      errors.push("Maskin/Merke må fylles ut");
    }
    if (!formData.beskrivelse.trim()) {
      errors.push("Beskrivelse må fylles ut");
    }
  }
  
  setValidationErrors(errors);
  return errors.length === 0;
};

  // Customer search select handler
  const handleCustomerSearchSelect = (customer: any) => {
    if (typeof customer === 'object' && customer.name) {
      handleInputChange("kunde", customer.name);
      if (customer.address) handleInputChange("adresse", customer.address);
      if (customer.tlf) handleInputChange("tlf", customer.tlf);
      if (customer.email) handleInputChange("mail", customer.email);
      if (customer.orgnr) handleInputChange("orgnr", customer.orgnr);
      if (customer.postnr && customer.sted) {
        handleInputChange("postnrSted", `${customer.postnr} ${customer.sted}`);
      }
    } else if (typeof customer === 'string') {
      handleInputChange("kunde", customer);
    }
  };

  // Amendo product select handler
  const handleAmendoProductSelect = (product: AmendoProduct | null) => {
    if (!product) return;
    
    const hasEmptyDefaultLine = formData.varelinjer.length === 1 && 
      !formData.varelinjer[0].varenr && 
      !formData.varelinjer[0].varebeskrivelse;
    
    const eksklMva = typeof product.priseksmva === 'number' ? 
      product.priseksmva.toFixed(2) : 
      parseFloat(product.priseksmva || 0).toFixed(2);
    
    const inklMva = typeof product.prisinklmva === 'number' ? 
      product.prisinklmva.toFixed(2) : 
      parseFloat(product.prisinklmva || 0).toFixed(2);
    
    const newVarelinje = { 
      dato: new Date().toISOString().split("T")[0], 
      antall: "1", 
      varenr: product.varenummer || "", 
      varebeskrivelse: product.navn || "", 
      eksklMva: eksklMva, 
      inklMva: inklMva, 
      rabatt: "", 
      sum: inklMva,
      levert: false
    };
    
    if (hasEmptyDefaultLine) {
      setFormData((prev) => ({ ...prev, varelinjer: [newVarelinje] }));
    } else {
      setFormData((prev) => ({ ...prev, varelinjer: [...prev.varelinjer, newVarelinje] }));
    }
    
    if (setHasUnsavedChanges) setHasUnsavedChanges(true);
  };

  // Varelinjer change handler
  const handleVarelinjeChange = (index: number, field: string, value: string) => {
    const newVarelinjer = [...formData.varelinjer];
    newVarelinjer[index] = { ...newVarelinjer[index], [field]: value };
    
    // Auto-calculate prices
    if (field === 'eksklMva') {
      const eksklMva = parseFloat(value) || 0;
      const inklMva = eksklMva * 1.25;
      newVarelinjer[index].inklMva = inklMva.toFixed(2);
    }
    if (field === 'inklMva') {
      const inklMva = parseFloat(value) || 0;
      const eksklMva = inklMva / 1.25;
      newVarelinjer[index].eksklMva = eksklMva.toFixed(2);
    }
    
      // Calculate sum med prosentrabatt
      const antall = parseFloat(newVarelinjer[index].antall) || 0;
      const inklMva = parseFloat(newVarelinjer[index].inklMva) || 0;
      const rabatt = parseFloat(newVarelinjer[index].rabatt) || 0;

      const bruttoSum = antall * inklMva;
      const rabattBelop = (bruttoSum * rabatt) / 100;
      const sum = bruttoSum - rabattBelop;
      newVarelinjer[index].sum = sum.toFixed(2);
    
    setFormData((prev) => ({ ...prev, varelinjer: newVarelinjer }));
    if (setHasUnsavedChanges) setHasUnsavedChanges(true);
  };

  const handleLevertChange = (index: number, levert: boolean) => {
  console.log(`Checkbox ${index} clicked, new value:`, levert);
  const newVarelinjer = [...formData.varelinjer];
  newVarelinjer[index] = { ...newVarelinjer[index], levert };
  setFormData((prev) => ({ ...prev, varelinjer: newVarelinjer }));
  if (setHasUnsavedChanges) setHasUnsavedChanges(true);
};

  // Add varelinjer  
  const addVarelinje = () => {
    const newVarelinje = { 
      dato: new Date().toISOString().split("T")[0], 
      antall: "", 
      varenr: "", 
      varebeskrivelse: "", 
      eksklMva: "", 
      inklMva: "", 
      rabatt: "", 
      sum: "",
      levert: false
    };
    setFormData((prev) => ({ ...prev, varelinjer: [...prev.varelinjer, newVarelinje] }));
    if (setHasUnsavedChanges) setHasUnsavedChanges(true);

      // Auto-resize alle textareas etter å ha lagt til ny linje
      setTimeout(() => {
      const textareas = document.querySelectorAll('textarea');
      textareas.forEach((textarea: HTMLTextAreaElement) => {
        textarea.style.height = '24px'; // Start liten
        if (textarea.value.trim()) {
          textarea.style.height = Math.max(24, textarea.scrollHeight) + 'px';
        }
      });
    }, 0);
  };

  // Remove varelinjer
  const removeVarelinje = (index: number) => {
    if (formData.varelinjer.length > 1) {
      const newVarelinjer = formData.varelinjer.filter((_, i) => i !== index);
      setFormData((prev) => ({ ...prev, varelinjer: newVarelinjer }));
      if (setHasUnsavedChanges) setHasUnsavedChanges(true);
    }   
  };

  // Calculate line sum
  const calculateLineSum = (linje: any): number => {
  const antall = parseFloat(linje.antall) || 0;
  const inklMva = parseFloat(linje.inklMva) || 0;
  const rabatt = parseFloat(linje.rabatt) || 0;
  
  const bruttoSum = antall * inklMva;
  const rabattBelop = (bruttoSum * rabatt) / 100;
  return bruttoSum - rabattBelop;
};

  // Calculate total sum
  const calculateTotalSum = () => {
    return formData.varelinjer.reduce((total, linje) => 
      total + calculateLineSum(linje), 0
    ).toFixed(2);
  };

  const moveVarelinjeUp = (index: number) => {
  if (index === 0) return; // Kan ikke flytte første linje opp
  const newVarelinjer = [...formData.varelinjer];
  [newVarelinjer[index - 1], newVarelinjer[index]] = [newVarelinjer[index], newVarelinjer[index - 1]];
  setFormData((prev) => ({ ...prev, varelinjer: newVarelinjer }));
  if (setHasUnsavedChanges) setHasUnsavedChanges(true);
};

// Flytt varelinje ned
const moveVarelinjeDown = (index: number) => {
  if (index === formData.varelinjer.length - 1) return; // Kan ikke flytte siste linje ned
  const newVarelinjer = [...formData.varelinjer];
  [newVarelinjer[index], newVarelinjer[index + 1]] = [newVarelinjer[index + 1], newVarelinjer[index]];
  setFormData((prev) => ({ ...prev, varelinjer: newVarelinjer }));
  if (setHasUnsavedChanges) setHasUnsavedChanges(true);
};

// Legg til ny varelinje etter en spesifikk index
const insertVarelinjeAfter = (index: number) => {
  const newVarelinje = { 
    dato: new Date().toISOString().split("T")[0], 
    antall: "", 
    varenr: "", 
    varebeskrivelse: "", 
    eksklMva: "", 
    inklMva: "", 
    rabatt: "", 
    sum: "",
    levert: false
  };
  const newVarelinjer = [...formData.varelinjer];
  newVarelinjer.splice(index + 1, 0, newVarelinje);
  setFormData((prev) => ({ ...prev, varelinjer: newVarelinjer }));
  if (setHasUnsavedChanges) setHasUnsavedChanges(true);
};

  // Create customer from form
  const createCustomerFromForm = async () => {
    const existingCustomer = customers.find((customer) => 
      customer.name.toLowerCase() === formData.kunde.toLowerCase()
    );
    
    if (existingCustomer) {
      const needsUpdate = 
        (!existingCustomer.tlf && formData.tlf) || 
        (!existingCustomer.address && formData.adresse) || 
        (!existingCustomer.email && formData.mail) || 
        (!existingCustomer.orgnr && formData.orgnr);
      
      if (needsUpdate) {
        const updatedCustomer = {
          ...existingCustomer,
          tlf: formData.tlf || existingCustomer.tlf,
          address: formData.adresse || existingCustomer.address,
          email: formData.mail || existingCustomer.email,
          orgnr: formData.orgnr || existingCustomer.orgnr,
          postnr: formData.postnrSted.split(" ")[0] || existingCustomer.postnr,
          sted: formData.postnrSted.split(" ").slice(1).join(" ") || existingCustomer.sted,
        };
        setCustomers(prev => prev.map(c => 
          c.id === existingCustomer.id ? updatedCustomer : c
        ));
        await supabaseService.saveCustomer(updatedCustomer);
        return updatedCustomer;
      }
      return existingCustomer;
    }

    if (formData.kunde.trim()) {
      const newCustomer = {
        id: parseInt(Date.now().toString().slice(-8)),
        name: formData.kunde, 
        address: formData.adresse, 
        postnr: formData.postnrSted.split(" ")[0] || "", 
        sted: formData.postnrSted.split(" ").slice(1).join(" ") || "", 
        orgnr: formData.orgnr, 
        tlf: formData.tlf, 
        email: formData.mail,
      };
      setCustomers((prev) => [...prev, newCustomer]);
      await supabaseService.saveCustomer(newCustomer);
      return newCustomer;
    }
    return null;
  };

  // Reset form
  const resetForm = () => {
    setFormData(initialFormData);
    setValidationErrors([]);
    setPhoneError("");
  };

  // Handle cancel
  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowCancelConfirm(true);
    } else {
      resetForm();
      clearEditingCase();
    }
  };

  // Confirm cancel
  const confirmCancel = () => {
    resetForm();
    clearEditingCase();
    setShowCancelConfirm(false);
    if (setHasUnsavedChanges) setHasUnsavedChanges(false);
  };

  // Handle reset
  const handleReset = () => {
    setShowResetConfirm(true);
  };

  // Confirm reset
  const confirmReset = () => {
    resetForm();
    setShowResetConfirm(false);
    if (showInfo) showInfo("Skjema nullstilt");
  };

  // Handle show history
  const handleShowHistory = async () => {
  if (!editingCase) {
    if (showWarning) showWarning("Ingen ordre valgt");
    return;
  }
  
  try {
    // Hent historikk fra databasen
    const history = await supabaseService.getCaseHistory(editingCase.id);
    
    if (history.length === 0) {
      if (showInfo) showInfo("Ingen historikk funnet for denne ordren");
      return;
    }
    
    // Vis historikk-dialog med data fra databasen
    setCaseHistory(history);
    setShowHistoryDialog(true);
  } catch (error) {
    console.error('Feil ved henting av historikk:', error);
    if (showError) showError("Kunne ikke laste historikk");
  }
};

  // Handle update order
  const handleUpdateOrder = async () => {
    if (isSaving) return;
    if (!validateForm()) {
      if (showError) {
        showError(
          "Vennligst fyll ut alle påkrevde felt før lagring", 
          "Valideringsfeil"
        );
      }
      return;
    }

    if (!editingCase || !originalCategory) return;

    setIsSaving(true);

    try {
      await createCustomerFromForm();
      let updatedFormData = { ...formData };
      const today = new Date().toISOString().split("T")[0];

      if (originalCategory === "Til bestilling" && 
          (formData.kategori === "Bestilt" || formData.kategori === "Service")) {
        updatedFormData.delebestillingDato = today;
      }
      
      if (originalCategory === "Klar til henting" || 
          originalCategory === "Klar til fakturering") {
        updatedFormData.utfortDato = today;
      }

      const caseToSave: Case = { 
        ...updatedFormData, 
        id: editingCase.id, 
        kategori: originalCategory, 
        opprettet: editingCase.opprettet,
        isVarekjop: isVarekjopMode,
      };

      const saved = await supabaseService.saveCase(caseToSave);
      if (saved) {
        setCases((prev) => prev.map((c) => 
          c.id === caseToSave.id ? caseToSave : c
        ));
        resetForm();
        clearEditingCase();
        if (showSuccess) {
          showSuccess(
            `Ordre #${editingCase.id} oppdatert i ${originalCategory}`, 
            "Ordre oppdatert"
          );
        }
      } else {
        if (showError) {
          showError("Kunne ikke oppdatere ordre. Prøv igjen.", "Oppdateringsfeil");
        }
      }
    } catch (error: any) {
      if (showError) {
        showError(
          error.message || "Teknisk feil ved oppdatering", 
          "Systemfeil"
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Handle save to category
  const handleSaveToCategory = async (kategori: string) => {
  // VIKTIG: Sjekk UMIDDELBART om vi allerede lagrer
  if (isSaving) {
    console.warn('⛔ Blokkerer dobbelt-klikk');
    return;
  }
  
  if (!validateForm()) {
    if (showError) {
      showError(
        "Vennligst fyll ut alle påkrevde felt før lagring", 
        "Valideringsfeil"
      );
    }
    return;
  }

  if (kategori === "Reklamasjon") {
    if (!formData.serienr || !formData.serienr.trim()) {
      if (showError) {
        showError(
          "Serienummer må være fylt ut før ordre kan flyttes til reklamasjon", 
          "Serienummer mangler"
        );
      }
      return;
    }
  }

  if (kategori === "Arkiv") {
    if (!formData.reklamasjon && !formData.betalt && !formData.faktura) {
      if (showError) {
        showError(
          "Ordre må være merket som 'Betalt' eller 'Faktura' før arkivering", 
          "Arkivering ikke tillatt"
        );
      }
      return;
    }
  }

  if (kategori === "Service") {
    setShowServiceLabelDialog(true);
    return;
  }

  if (kategori === "Klar til henting" && editingCase && editingCase.kategori === "Bestilt") {
    setShowBestillingLabelDialog(true);
    return;
  }

  if (kategori === "Klar til henting") {
    setSmsCustomerInfo({ name: formData.kunde, phone: formData.tlf });
    setShowSmsReminder(true);
    return;
  }

  // Start lagring
  completeSaveToCategory(kategori);
};

// Handle update order - Oppdater ordre i samme kategori
const handleSaveToOriginalCategory = async () => {
  if (isSaving) return;
  if (!validateForm()) {
    if (showError) {
      showError(
        "Vennligst fyll ut alle påkrevde felt før lagring", 
        "Valideringsfeil"
      );
    }
    return;
  }

  if (!editingCase || !originalCategory) return;

  setIsSaving(true);

  try {
    await createCustomerFromForm();
    let updatedFormData = { ...formData };
    const today = new Date().toISOString().split("T")[0];

    if (originalCategory === "Til bestilling" && 
        (formData.kategori === "Bestilt" || formData.kategori === "Service")) {
      updatedFormData.delebestillingDato = today;
    }
    
    if (originalCategory === "Klar til henting" || 
        originalCategory === "Klar til fakturering") {
      updatedFormData.utfortDato = today;
    }

    const caseToSave: Case = { 
      ...updatedFormData, 
      id: editingCase.id, 
      kategori: originalCategory, 
      opprettet: editingCase.opprettet 
    };

    const saved = await supabaseService.saveCase(caseToSave);
    if (saved) {
      setCases((prev) => prev.map((c) => 
        c.id === caseToSave.id ? caseToSave : c
      ));
      resetForm();
      clearEditingCase();
      if (showSuccess) {
        showSuccess(
          `Ordre #${editingCase.id} oppdatert i ${originalCategory}`, 
          "Ordre oppdatert"
        );
      }
    } else {
      if (showError) {
        showError("Kunne ikke oppdatere ordre. Prøv igjen.", "Oppdateringsfeil");
      }
    }
  } catch (error: any) {
    if (showError) {
      showError(
        error.message || "Teknisk feil ved oppdatering", 
        "Systemfeil"
      );
    }
  } finally {
    setIsSaving(false);
  }
};

// Complete save to category
const completeSaveToCategory = async (kategori: string) => {
  // 1. Sjekk om vi nylig har lagret
  const now = Date.now();
  if (now - lastSaveTime.current < MIN_TIME_BETWEEN_SAVES) {
    console.warn('For rask lagring, ignorerer');
    if (showWarning) {
      showWarning("Vennligst vent litt før du prøver igjen", "For mange klikk");
    }
    return;
  }
  
  // 2. Sjekk om vi allerede lagrer
  if (isSaving) {
    console.log('Allerede i gang med lagring, avbryter');
    if (showWarning) {
      showWarning("Lagring pågår allerede, vennligst vent...", "Vennligst vent");
    }
    return;
  }
  
  // 3. Sett tidsstempel og isSaving UMIDDELBART
  lastSaveTime.current = now;
  setIsSaving(true);
  
  // 4. Vis visuell feedback UMIDDELBART
  console.log('🔄 Starter lagring til kategori:', kategori);
  
  try {
    // Generer unik ID basert på timestamp + random
    const uniqueId = Date.now() + Math.floor(Math.random() * 1000);
    
    await createCustomerFromForm();
    let updatedFormData = { ...formData };
    const today = new Date().toISOString().split("T")[0];

    if (editingCase && 
        editingCase.kategori === "Til bestilling" && 
        (kategori === "Bestilt" || kategori === "Service")) {
      updatedFormData.delebestillingDato = today;
    }
    
    if (kategori === "Klar til henting" || kategori === "Klar til fakturering") {
      updatedFormData.utfortDato = today;
    }

    const caseToSave: Case = { 
      ...updatedFormData, 
      id: editingCase ? editingCase.id : uniqueId,
      kategori: kategori, 
      opprettet: editingCase ? editingCase.opprettet : new Date().toISOString(),
      isVarekjop: isVarekjopMode,
    };

    console.log('💾 Lagrer ordre:', caseToSave.id);
    const saved = await supabaseService.saveCase(caseToSave);
    
    if (saved) {
      if (editingCase) {
        setCases((prev) => prev.map((c) => 
          c.id === caseToSave.id ? caseToSave : c
        ));
      } else {
        setCases((prev) => [...prev, caseToSave]);
      }
      
      console.log('✅ Lagring fullført!');
      
      // Vent litt før vi nullstiller, så brukeren ser at noe skjedde
      setTimeout(() => {
        resetForm();
        clearEditingCase();
        if (showSuccess) {
          showSuccess(
            editingCase 
              ? `Ordre #${caseToSave.id} flyttet til ${kategori}` 
              : `Ny ordre opprettet i ${kategori}`,
            "Lagring fullført"
          );
        }
      }, 500); // 500ms forsinkelse gir bedre brukeropplevelse
      
    } else {
      throw new Error("Kunne ikke lagre ordre");
    }
  } catch (error: any) {
    console.error('❌ Lagringsfeil:', error);
    if (showError) {
      showError(
        error.message || "Teknisk feil ved lagring", 
        "Lagringsfeil"
      );
    }
  } finally {
    // Vent litt ekstra før vi re-enabler knappene
    setTimeout(() => {
      setIsSaving(false);
      console.log('🔓 Knapper aktive igjen');
    }, 1000); // 1 sekund total forsinkelse
  }
};

  // Handle delete case
  const handleDeleteCase = async () => {
  if (!editingCase) return;
  
  const confirm = window.confirm(
    `🗑️ FLYTT TIL PAPIRKURV\n\n` +
    `Vil du flytte ordre #${editingCase.id} til papirkurven?\n\n` +
    `Ordren kan gjenopprettes fra papirkurven i 30 dager.`
  );

  if (!confirm) return;

  try {
    const success = await supabaseService.moveToTrash(editingCase);
    
    if (success) {
      // Fjern fra cases
      setCases(prev => prev.filter(c => c.id !== editingCase.id));
      
      // Oppdater papirkurv
      if (setDeletedCases) {  // ✅ Fjern && deletedCases
        try {
          const updatedDeletedCases = await supabaseService.loadDeletedCases();
          setDeletedCases(updatedDeletedCases);
        } catch (error) {
          console.log('Kunne ikke oppdatere papirkurv-visning:', error);
        }
      }
      
      clearEditingCase();
      
      if (showSuccess) {
        showSuccess(
          `Ordre #${editingCase.id} flyttet til papirkurven`, 
          "Flyttet til papirkurv"
        );
      }
    } else {
      if (showError) {
        showError("Kunne ikke flytte ordre til papirkurv. Prøv igjen.", "Feil");
      }
    }
  } catch (error) {
    console.error('Feil ved flytting til papirkurv:', error);
    if (showError) {
      showError("Teknisk feil ved sletting", "Systemfeil");
    }
  }
};

// Form validation check - bruk useMemo for å unngå re-renders
const isFormValid = React.useMemo(() => {
  const errors: string[] = [];
  
  if (!formData.kunde.trim()) {
    errors.push("Kunde må fylles ut");
  }
  
  // Varekjøp-modus: Hopp over validering
  if (!isVarekjopMode) {
    if (!formData.tlf.trim()) {
      errors.push("Telefonnummer må fylles ut");
    } else if (formData.tlf.replace(/\s/g, "").length !== 8) {
      errors.push("Telefonnummer må være 8 siffer");
    }
    if (!formData.maskinMerke.trim()) {
      errors.push("Maskin/Merke må fylles ut");
    }
    if (!formData.beskrivelse.trim()) {
      errors.push("Beskrivelse må fylles ut");
    }
  }
  
  return errors.length === 0;
}, [formData.kunde, formData.tlf, formData.maskinMerke, formData.beskrivelse, isVarekjopMode]);

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      {/* Header */}
      <div className="p-4 border-b bg-gradient-to-r from-green-50 to-blue-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Plus className="text-green-600" size={24} />
            <h2 className="text-xl font-semibold text-gray-900">
              {editingCase ? `Redigerer ordre #${editingCase.id}` : "Ny registrering"}
            </h2>
            {!editingCase && (
              <button
                onClick={() => setIsVarekjopMode(!isVarekjopMode)}
                className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                  isVarekjopMode 
                    ? "bg-green-500 text-white hover:bg-green-600" 
                    : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                }`}
              >
                {isVarekjopMode ? "✓ Varekjøp" : "Varekjøp"}
              </button>
            )}
            {editingCase && originalCategory && (
              <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                Fra: {originalCategory}
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            {/* Service label print button */}
            {editingCase && editingCase.kategori === "Service" && (
              <button 
                onClick={() => printServiceLabel(editingCase)} 
                className="flex items-center space-x-2 px-3 py-1.5 bg-blue-600 
                          text-white rounded-lg hover:bg-blue-700 text-sm" 
                title="Skriv ut merkelapp"
              >
                <Printer size={16} />
                <span>Merkelapp</span>
              </button>
            )}
            
            {/* Action buttons for editing case */}
            {editingCase && (
              <>
                <button 
                  onClick={handleDeleteCase} 
                  className="flex items-center space-x-2 px-3 py-1.5 bg-red-600 
                            text-white rounded-lg hover:bg-red-700 text-sm" 
                  title="Slett denne ordren"
                >
                  <Trash2 size={16} />
                  <span>Slett ordre</span>
                </button>
                
                <button 
                  onClick={handleShowHistory} 
                  className="flex items-center space-x-2 px-3 py-1.5 bg-gray-100 
                            text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
                >
                  <History size={16} />
                  <span>Historikk</span>
                </button>
                
                <button 
                  onClick={handleCancel} 
                  className="flex items-center space-x-2 px-3 py-1.5 bg-gray-100 
                            text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
                >
                  <X size={16} />
                  <span>Avbryt</span>
                </button>
              </>
            )}
            
            {/* Reset button */}
            <button 
              onClick={handleReset} 
              className="flex items-center space-x-2 px-3 py-1.5 bg-gray-100 
                        text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
            >
              <X size={16} />
              <span>Nullstill</span>
            </button>
          </div>
        </div>
      </div>
      {/* Main form content */}
      <div className="p-4">
        {/* Validation errors */}
        {validationErrors.length > 0 && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start space-x-2">
              <AlertCircle className="text-red-600 mt-0.5" size={16} />
              <div>
                <p className="text-sm font-medium text-red-800">
                  Vennligst rett følgende feil:
                </p>
                <ul className="list-disc list-inside text-sm text-red-700 mt-1">
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {/* Customer and date row */}
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-8">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kunde <span className="text-red-500">*</span>
              </label>
              {formData.kunde && customers.some(c => c.name === formData.kunde) ? (
                <div className="relative">
                  <input 
                    type="text" 
                    value={formData.kunde} 
                    readOnly 
                    className="w-full px-3 py-2 border border-gray-300 
                              bg-gray-50 rounded-lg" 
                  />
                  <button 
                    type="button" 
                    onClick={() => handleInputChange("kunde", "")} 
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 
                              text-gray-400 hover:text-gray-600" 
                    title="Endre kunde"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <CustomerSearch 
                  customers={customers} 
                  currentValue={formData.kunde} 
                  onValueChange={(value) => handleInputChange("kunde", value)} 
                  onCustomerSelect={handleCustomerSearchSelect} 
                />
              )}
            </div>
            <div className="col-span-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dato
              </label>
              <input 
                type="date" 
                value={formData.dato} 
                onChange={(e) => handleInputChange("dato", e.target.value)} 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                          focus:ring-2 focus:ring-green-500 focus:border-transparent" 
              />
            </div>
          </div>

         {/* Address row */}
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adresse
              </label>
              <input 
                type="text" 
                value={formData.adresse} 
                onChange={(e) => handleInputChange("adresse", e.target.value)} 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                          focus:ring-2 focus:ring-green-500 focus:border-transparent" 
                placeholder="Gateadresse" 
              />
            </div>
            <div className="col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Postnr/Sted
              </label>
              <input 
                type="text" 
                value={formData.postnrSted} 
                onChange={(e) => handleInputChange("postnrSted", e.target.value)} 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                          focus:ring-2 focus:ring-green-500 focus:border-transparent" 
                placeholder="4056 Tananger" 
              />
            </div>
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Avd
              </label>
              <input 
                type="text" 
                value={formData.avd} 
                onChange={(e) => handleInputChange("avd", e.target.value)} 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                          focus:ring-2 focus:ring-green-500 focus:border-transparent" 
                placeholder="Avd" 
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Org.nr
              </label>
              <input 
                type="text" 
                value={formData.orgnr} 
                onChange={(e) => handleInputChange("orgnr", e.target.value)} 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                          focus:ring-2 focus:ring-green-500 focus:border-transparent" 
                placeholder="Org.nr" 
              />
            </div>
          </div>

          {/* Contact row */}
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Telefon {!isVarekjopMode && <span className="text-red-500">*</span>}
              </label>
              <input 
                  type="tel" 
                  value={formatPhoneNumber(formData.tlf)} 
                  onChange={(e) => handlePhoneChange(e.target.value)} 
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 
                            focus:ring-green-500 focus:border-transparent 
                            ${phoneError ? 'border-red-500' : 'border-gray-300'}`} 
                  placeholder="123 45 678" 
                  maxLength={10}  // 8 siffer + 2 mellomrom
                />
              {phoneError && (
                <p className="text-xs text-red-500 mt-0.5">{phoneError}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mail
              </label>
              <input 
                type="email" 
                value={formData.mail} 
                onChange={(e) => handleInputChange("mail", e.target.value)} 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                          focus:ring-2 focus:ring-green-500 focus:border-transparent" 
                placeholder="post@kunde.no" 
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Merking
              </label>
              <input 
                type="text" 
                value={formData.merking} 
                onChange={(e) => handleInputChange("merking", e.target.value)} 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                          focus:ring-2 focus:ring-green-500 focus:border-transparent" 
                placeholder="Merking" 
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ref
              </label>
              <input 
                type="text" 
                value={formData.ref} 
                onChange={(e) => handleInputChange("ref", e.target.value)} 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                          focus:ring-2 focus:ring-green-500 focus:border-transparent" 
                placeholder="Kontaktperson" 
              />
            </div>
          </div>
          {/* Machine info row */}
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Maskin/Merke {!isVarekjopMode && <span className="text-red-500">*</span>}
              </label>
              <input 
                type="text" 
                value={formData.maskinMerke} 
                onChange={(e) => handleInputChange("maskinMerke", e.target.value)} 
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 
                           focus:ring-green-500 focus:border-transparent 
                           ${validationErrors.includes("Maskin/Merke må fylles ut") ? 
                             "border-red-500" : "border-gray-300"}`} 
                placeholder="F.eks: Honda plenklipper" 
              />
            </div>
            <div className="col-span-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Serienr
              </label>
              <input 
                type="text" 
                value={formData.serienr} 
                onChange={(e) => handleInputChange("serienr", e.target.value)} 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                          focus:ring-2 focus:ring-green-500 focus:border-transparent" 
                placeholder="Serienummer" 
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Årsmodell
              </label>
              <input 
                type="text" 
                value={formData.arsmodel} 
                onChange={(e) => handleInputChange("arsmodel", e.target.value)} 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                          focus:ring-2 focus:ring-green-500 focus:border-transparent" 
                placeholder="2024" 
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Beskrivelse {!isVarekjopMode && <span className="text-red-500">*</span>}
            </label>
            <textarea 
              value={formData.beskrivelse} 
              onChange={(e) => handleInputChange("beskrivelse", e.target.value)} 
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 
                         focus:ring-green-500 focus:border-transparent 
                         ${validationErrors.includes("Beskrivelse må fylles ut") ? 
                           "border-red-500" : "border-gray-300"}`} 
              rows={3} 
              placeholder="Beskriv problemet eller servicebe hovet..." 
            />
          </div>

          {/* Varelinjer section */}
          <div className="border-t pt-4">
            <div className="mb-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-medium text-gray-900">Varelinjer</h3>
                <div className="flex items-center space-x-3">
                  <div className="text-sm text-gray-500">
                    {productsLoading && "Laster produkter..."}
                    {!productsLoading && productsLoaded && 
                      `${amendoProducts.length} produkter tilgjengelig`}
                    {!productsLoading && !productsLoaded && "Ingen produkter lastet"}
                  </div>
                  <button 
                    type="button" 
                    onClick={addVarelinje} 
                    className="flex items-center space-x-1 px-3 py-1.5 bg-green-600 
                              text-white rounded-lg hover:bg-green-700 text-sm"
                  >
                    <Plus size={16} />
                    <span>Legg til linje</span>
                  </button>
                </div>
              </div>

              {/* Product search */}
              <div className="w-full max-w-2xl mb-3">
                {productsLoading ? (
                  <div className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                                  bg-gray-50 text-gray-500">
                    Laster produktsøk...
                  </div>
                ) : (
                  <ProductSearch 
                    products={amendoProducts} 
                    onProductSelect={handleAmendoProductSelect} 
                  />
                )}
              </div>
            </div>
            {/* Varelinjer table */}
            <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-100 border-b border-gray-300">
                    <th className="text-center px-2 py-2 text-xs font-medium text-gray-700 
                                  border-r border-gray-200" 
                        style={{ width: '40px' }}>
                      Levert
                    </th>
                    <th className="text-left px-2 py-2 text-xs font-medium text-gray-700 
                                  border-r border-gray-200" 
                        style={{ width: '100px' }}>
                      Dato
                    </th>
                    <th className="text-center px-2 py-2 text-xs font-medium text-gray-700 
                                  border-r border-gray-200" 
                        style={{ width: '60px' }}>
                      Antall
                    </th>
                    <th className="text-left px-2 py-2 text-xs font-medium text-gray-700 
                                  border-r border-gray-200" 
                        style={{ width: '140px' }}> {/* ØKET FRA 100px TIL 140px */}
                      Varenr
                    </th>
                    <th className="text-left px-2 py-2 text-xs font-medium text-gray-700 
                                  border-r border-gray-200">
                      Beskrivelse
                    </th>
                    <th className="text-right px-2 py-2 text-xs font-medium text-gray-700 
                                  border-r border-gray-200" 
                        style={{ width: '80px' }}>
                      Eks. mva
                    </th>
                    <th className="text-right px-2 py-2 text-xs font-medium text-gray-700 
                                  border-r border-gray-200" 
                        style={{ width: '80px' }}>
                      Inkl. mva
                    </th>
                    <th className="text-center px-2 py-2 text-xs font-medium text-gray-700 
                                  border-r border-gray-200" 
                        style={{ width: '70px' }}>
                      Rabatt %
                    </th>
                    <th className="text-right px-2 py-2 text-xs font-medium text-gray-700 
                                  border-r border-gray-200" 
                        style={{ width: '90px' }}>
                      Sum
                    </th>
                    <th className="text-center px-1 py-2" style={{ width: '30px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {formData.varelinjer.map((linje, index) => (
                    <tr key={index} 
                        className={`border-b border-gray-200 transition-colors ${
                          linje.levert 
                            ? 'bg-green-50 hover:bg-green-100' 
                            : 'hover:bg-gray-50'
                        }`}>
                      
                      {/* Checkbox kolonne */}
                      <td className="px-1 py-1 border-r border-gray-200 text-center">
                        <input 
                          type="checkbox" 
                          checked={linje.levert || false}
                          onChange={(e) => handleLevertChange(index, e.target.checked)}
                          className="w-4 h-4 text-green-600 rounded focus:ring-green-500 
                                    border-gray-300 cursor-pointer"
                          title={linje.levert ? "Markert som levert" : "Klikk for å markere som levert"}
                        />
                      </td>
                      
                      {/* Dato kolonne */}
                      <td className="px-1 py-1 border-r border-gray-200">
                        <input 
                          type="date" 
                          value={linje.dato} 
                          onChange={(e) => handleVarelinjeChange(index, "dato", e.target.value)} 
                          className={`w-full px-1 py-1 text-sm border-0 bg-transparent 
                                    focus:outline-none focus:ring-1 focus:ring-green-500 rounded
                                    ${linje.levert ? 'text-green-700' : ''}`}
                        />
                      </td>
                      
                      {/* Antall kolonne */}
                      <td className="px-1 py-1 border-r border-gray-200">
                        <input 
                          type="number" 
                          value={linje.antall} 
                          onChange={(e) => handleVarelinjeChange(index, "antall", e.target.value)} 
                          className={`w-full px-1 py-1 text-sm text-center border-0 bg-transparent 
                                    focus:outline-none focus:ring-1 focus:ring-green-500 rounded
                                    ${linje.levert ? 'text-green-700' : ''}`}
                          placeholder="0" 
                        />
                      </td>
                      
                      {/* Varenr kolonne - FORBEDRET BREDDE */}
                      <td className="px-1 py-1 border-r border-gray-200">
                        <input 
                          type="text" 
                          value={linje.varenr} 
                          onChange={(e) => handleVarelinjeChange(index, "varenr", e.target.value)} 
                          className={`w-full px-1 py-1 text-sm border-0 bg-transparent 
                                    focus:outline-none focus:ring-1 focus:ring-green-500 rounded
                                    ${linje.levert ? 'text-green-700' : ''}`}
                          placeholder="Varenr" 
                          style={{ minWidth: '140px' }} // EKSPLISITT MIN-BREDDE
                        />
                      </td>
                      
                      {/* Beskrivelse kolonne – UTVIDBAR TEXTAREA */}
                      <td className="px-1 py-1 border-r border-gray-200 align-top">
                        <textarea
                          value={linje.varebeskrivelse}
                          onChange={(e) => {
                            handleVarelinjeChange(index, "varebeskrivelse", e.target.value);
                          }}
                          onInput={(e) => {
                            const target = e.target as HTMLTextAreaElement;
                            // Alltid start med minimum høyde
                            target.style.height = '24px';
                            // Hvis det er innhold, juster til scrollHeight
                            if (target.value.trim()) {
                              target.style.height = Math.max(24, target.scrollHeight) + 'px';
                            }
                          }}
                          className={`w-full px-1 py-1 text-sm border-0 bg-transparent
                                      focus:outline-none focus:ring-1 focus:ring-green-500 rounded
                                      resize-none overflow-hidden
                                      ${linje.levert ? 'text-green-700' : ''}`}
                          placeholder="Beskrivelse"
                          style={{ height: '24px', minHeight: '24px' }}
                          ref={(el) => {
                            if (el) {
                              // Sett initial høyde når element rendres
                              el.style.height = '24px';
                              if (el.value.trim()) {
                                el.style.height = Math.max(24, el.scrollHeight) + 'px';
                              }
                            }
                          }}
                        />
                      </td>

                      
                      {/* Eksklusive mva kolonne */}
                      <td className="px-1 py-1 border-r border-gray-200 align-top">
                        <input 
                          type="number" 
                          step="0.01" 
                          value={linje.eksklMva} 
                          onChange={(e) => handleVarelinjeChange(index, "eksklMva", e.target.value)} 
                          className={`w-full px-1 py-1 text-sm text-right border-0 bg-transparent 
                                    focus:outline-none focus:ring-1 focus:ring-green-500 rounded
                                    ${linje.levert ? 'text-green-700' : ''}`}
                          placeholder="0.00" 
                        />
                      </td>
                      
                      {/* Inclusive mva kolonne */}
                      <td className="px-1 py-1 border-r border-gray-200 align-top">
                        <input 
                          type="number" 
                          step="0.01" 
                          value={linje.inklMva} 
                          onChange={(e) => handleVarelinjeChange(index, "inklMva", e.target.value)} 
                          className={`w-full px-1 py-1 text-sm text-right border-0 bg-transparent 
                                    focus:outline-none focus:ring-1 focus:ring-green-500 rounded
                                    ${linje.levert ? 'text-green-700' : ''}`}
                          placeholder="0.00" 
                        />
                      </td>
                      
                      {/* Rabatt kolonne */}
                      <td className="px-1 py-1 border-r border-gray-200 align-top">
                        <input 
                          type="number" 
                          step="0.01" 
                          value={linje.rabatt} 
                          onChange={(e) => handleVarelinjeChange(index, "rabatt", e.target.value)} 
                          className={`w-full px-1 py-1 text-sm text-center border-0 bg-transparent 
                                    focus:outline-none focus:ring-1 focus:ring-green-500 rounded
                                    ${linje.levert ? 'text-green-700' : ''}`}
                          placeholder="0" 
                        />
                      </td>
                      
                      {/* Sum kolonne */}
                      <td className="px-1 py-1 border-r border-gray-200 align-top">
                        <input 
                          type="text" 
                          value={linje.sum} 
                          readOnly 
                          className={`w-full px-1 py-1 text-sm text-right border-0 
                                    bg-gray-50 font-medium
                                    ${linje.levert ? 'text-green-700 bg-green-50' : ''}`}
                          placeholder="0.00" 
                        />
                      </td>

                      {/* Handlinger kolonne - opp/ned/legg til */}
                      <td className="px-1 py-1 text-center align-top">
                        <div className="flex items-center justify-center space-x-1">
                          {/* Flytt opp */}
                          <button
                            type="button"
                            onClick={() => moveVarelinjeUp(index)}
                            disabled={index === 0}
                            className={`p-1 rounded hover:bg-gray-200 transition-colors ${
                              index === 0 ? 'opacity-30 cursor-not-allowed' : ''
                            }`}
                            title="Flytt opp"
                          >
                            <ChevronUp size={14} />
                          </button>
                          
                          {/* Flytt ned */}
                          <button
                            type="button"
                            onClick={() => moveVarelinjeDown(index)}
                            disabled={index === formData.varelinjer.length - 1}
                            className={`p-1 rounded hover:bg-gray-200 transition-colors ${
                              index === formData.varelinjer.length - 1 ? 'opacity-30 cursor-not-allowed' : ''
                            }`}
                            title="Flytt ned"
                          >
                            <ChevronDown size={14} />
                          </button>
                          
                          {/* Legg til ny linje under */}
                          <button
                            type="button"
                            onClick={() => insertVarelinjeAfter(index)}
                            className="p-1 rounded hover:bg-green-100 transition-colors text-green-600"
                            title="Legg til ny linje under"
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                      </td>
                      
                      {/* Slett knapp kolonne */}
                      <td className="px-1 py-1 text-center align-top">
                        {formData.varelinjer.length > 1 && (
                          <button 
                            type="button" 
                            onClick={() => removeVarelinje(index)} 
                            className="text-red-500 hover:text-red-700 mt-0.5"
                          >
                            <X size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                
                {/* Footer with totals */}
                <tfoot>
                  <tr className="bg-gray-100 border-t-2 border-gray-300">
                    <td className="px-2 py-2"></td>{/* Tom celle for checkbox */}
                    <td colSpan={7} className="px-2 py-2 text-right font-medium text-gray-700">
                      Total sum:
                    </td>
                    <td className="px-2 py-2 text-right font-bold text-gray-900 
                                  border-x border-gray-200">
                      {calculateTotalSum()}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Delebestilling dato */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Delebestilling dato
            </label>
            <input 
              type="date" 
              value={formData.delebestillingDato} 
              onChange={(e) => handleInputChange("delebestillingDato", e.target.value)} 
              onClick={(e) => e.target.showPicker && e.target.showPicker()}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                        focus:ring-2 focus:ring-green-500 focus:border-transparent
                        cursor-pointer hover:bg-gray-50 transition-colors" 
            />
          </div>
          {/* Service information section */}
          <div className="border-t pt-4">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Service informasjon</h3>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="relative" ref={utlevertAvRef}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Utlevert av
                </label>
                <div className="relative">
                  <input 
                    type="text" 
                    value={formData.utlevertAv} 
                    onChange={(e) => handleInputChange("utlevertAv", e.target.value)} 
                    onFocus={() => setShowUtlevertAvDropdown(true)} 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                              focus:ring-2 focus:ring-green-500 focus:border-transparent" 
                    placeholder="Velg person" 
                  />
                  <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 
                                         text-gray-400 pointer-events-none" 
                               size={16} />
                </div>
                {showUtlevertAvDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 
                                  rounded-lg shadow-lg">
                    {utlevertAvPersons.map((person) => (
                      <div 
                        key={person} 
                        onClick={() => { 
                          handleInputChange("utlevertAv", person); 
                          setShowUtlevertAvDropdown(false); 
                        }} 
                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                      >
                        {person}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kommentar
                </label>
                <textarea 
                  value={formData.kommentar} 
                  onChange={(e) => {
                    handleInputChange("kommentar", e.target.value);
                  }}
                  onInput={(e) => {
                    const target = e.target as HTMLTextAreaElement;
                    target.style.height = '40px';
                    if (target.value.trim()) {
                      target.style.height = Math.max(40, target.scrollHeight) + 'px';
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                            focus:ring-2 focus:ring-green-500 focus:border-transparent
                            resize-none overflow-hidden" 
                  placeholder="Intern kommentar"
                  style={{ height: '40px', minHeight: '40px' }}
                  ref={(el) => {
                    if (el) {
                      el.style.height = '40px';
                      if (el.value.trim()) {
                        el.style.height = Math.max(40, el.scrollHeight) + 'px';
                      }
                    }
                  }}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Utført dato
                </label>
                <input 
                  type="date" 
                  value={formData.utfortDato} 
                  onChange={(e) => handleInputChange("utfortDato", e.target.value)} 
                  onClick={(e) => e.target.showPicker && e.target.showPicker()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                            focus:ring-2 focus:ring-green-500 focus:border-transparent
                            cursor-pointer hover:bg-gray-50 transition-colors" 
                />
              </div>
              
              <div className="relative" ref={serviceRepRef}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Service rep utført
                </label>
                <div className="relative">
                  <input 
                    type="text" 
                    value={formData.serviceRepUtført} 
                    onChange={(e) => handleInputChange("serviceRepUtført", e.target.value)} 
                    onFocus={() => setShowServiceRepDropdown(true)} 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg 
                              focus:ring-2 focus:ring-green-500 focus:border-transparent" 
                    placeholder="Velg person" 
                  />
                  <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 
                                         text-gray-400 pointer-events-none" 
                               size={16} />
                </div>
                {showServiceRepDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 
                                  rounded-lg shadow-lg">
                    {serviceRepPersons.map((person) => (
                      <div 
                        key={person} 
                        onClick={() => { 
                          handleInputChange("serviceRepUtført", person); 
                          setShowServiceRepDropdown(false); 
                        }} 
                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                      >
                        {person}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Payment status section */}
          <div className="border-t pt-4">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Betalingsstatus</h3>
            <div className="flex items-center space-x-6">
              <label className="flex items-center">
                <input 
                  type="checkbox" 
                  checked={formData.betalt} 
                  onChange={(e) => handleInputChange("betalt", e.target.checked)} 
                  className="mr-2" 
                />
                <span className="text-sm">Betalt</span>
              </label>
              <label className="flex items-center">
                <input 
                  type="checkbox" 
                  checked={formData.faktura} 
                  onChange={(e) => handleInputChange("faktura", e.target.checked)} 
                  className="mr-2" 
                />
                <span className="text-sm">Faktura</span>
              </label>
              <label className="flex items-center">
                <input 
                  type="checkbox" 
                  checked={formData.reklamasjon} 
                  onChange={(e) => handleInputChange("reklamasjon", e.target.checked)} 
                  className="mr-2" 
                />
                <span className="text-sm text-red-600">Reklamasjon</span>
              </label>
            </div>
          </div>
        </div>
      </div>
      {/* Footer with action buttons */}
      <div className="p-4 border-t bg-gray-50">
        {/* Update button for editing existing case */}
        {editingCase && originalCategory && (
          <div className="mb-4">
            <button 
              onClick={handleUpdateOrder} 
              disabled={!isFormValid || isSaving}
              className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white 
                        py-4 px-6 rounded-lg hover:from-green-700 hover:to-green-800 
                        transition-all duration-200 font-semibold text-lg shadow-lg"
            >
              💾 Oppdater ordre #{editingCase.id} i {originalCategory}
            </button>
          </div>
        )}

        {/* Category selection buttons */}
        <div className="space-y-3">
          <div className="text-sm font-medium text-gray-700">
            {editingCase ? "Flytt ordre til ny kategori:" : "Velg kategori for ny ordre"}
          </div>
          <div className="flex space-x-2">
            {categories.map((category) => (
              <button 
                key={category.id} 
                onClick={() => handleSaveToCategory(category.id)} 
                disabled={isSaving}
                className={`flex items-center space-x-2 px-4 py-2 ${category.color} 
                           text-white rounded-lg transition-all
                           ${isSaving 
                             ? 'opacity-50 cursor-not-allowed' 
                             : 'hover:opacity-90'
                           }`}
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    <span>Lagrer...</span>
                  </>
                ) : (
                  <>
                    <category.icon size={16} />
                    <span>{category.name}</span>
                  </>
                )}
              </button>
            ))}
          </div>
          
          {/* Visuell indikator når knapp er deaktivert */}
          {isSaving && (
            <div className="text-sm text-gray-600 bg-yellow-50 p-2 rounded flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-yellow-600 border-t-transparent"></div>
              <span>Lagrer ordre, vennligst vent...</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Reset confirmation dialog */}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold mb-4">Bekreft nullstilling</h3>
            <p className="text-gray-600 mb-6">
              Er du sikker på at du vil nullstille skjemaet? Alle data vil gå tapt.
            </p>
            <div className="flex space-x-3">
              <button 
                onClick={confirmReset} 
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Nullstill
              </button>
              <button 
                onClick={() => setShowResetConfirm(false)} 
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel confirmation dialog */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold mb-4">Ulagrede endringer</h3>
            <p className="text-gray-600 mb-6">
              Du har ulagrede endringer. Er du sikker på at du vil avbryte redigeringen?
            </p>
            <div className="flex space-x-3">
              <button 
                onClick={confirmCancel} 
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Avbryt redigering
              </button>
              <button 
                onClick={() => setShowCancelConfirm(false)} 
                className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                Fortsett redigering
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SMS reminder dialog */}
      {showSmsReminder && smsCustomerInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center space-x-3 mb-4">
              <MessageSquare className="text-blue-600" size={24} />
              <h3 className="text-lg font-semibold">SMS Påminnelse</h3>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-800">
                Husk å sende SMS til <strong>{smsCustomerInfo.name}</strong> på telefon{' '}
                <strong>{smsCustomerInfo.phone}</strong> om at ordren er klar for henting.
              </p>
            </div>
            <button 
              onClick={() => setShowSmsReminder(false)} 
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              OK, forstått
            </button>
          </div>
        </div>
      )}

      {/* History dialog */}
      {showHistoryDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                Historikk for ordre #{editingCase?.id} ({caseHistory.length} hendelser)
              </h3>
              <button 
                onClick={() => setShowHistoryDialog(false)} 
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>
            {caseHistory.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                Ingen historikk registrert for denne ordren
              </p>
            ) : (
              <div className="space-y-3">
                {caseHistory.map((entry, index) => (
                  <div key={index} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className="font-semibold">{entry.action}</span>
                        <span className="ml-3 text-sm text-gray-500">
                          {new Date(entry.created_at).toLocaleString("nb-NO")}
                        </span>
                      </div>
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                        {entry.category}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Utført av: {entry.user_name}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Service Label Dialog */}
      <ServiceLabelDialog
        isOpen={showServiceLabelDialog}
        onClose={async () => {
          setShowServiceLabelDialog(false);
          await completeSaveToCategory("Service");  // Lagre til Service uten merkelapp
        }}
        onConfirm={async () => {
          await completeSaveToCategory("Service");  // Lagre til Service med merkelapp
          const caseDataForPrint = { 
            ...formData, 
            id: editingCase ? editingCase.id : Date.now() 
          };
          printServiceLabel(caseDataForPrint);
        }}
        customerName={formData.kunde}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirmDialog}
        onClose={() => setShowDeleteConfirmDialog(false)}
        onConfirm={async () => {
          try {
            const deleted = await supabaseService.deleteCase(editingCase!.id);
            if (deleted) {
              setCases((prev) => prev.filter((c) => c.id !== editingCase!.id));
              resetForm();
              clearEditingCase();
              if (showSuccess) showSuccess(`Ordre #${editingCase!.id} er slettet`);
            } else {
              if (showError) showError("Kunne ikke slette ordren. Prøv igjen.", "Sletting feilet");
            }
          } catch (error: any) {
            if (showError) showError(error.message || "Teknisk feil ved sletting", "Systemfeil");
          }
        }}
        title="Slett ordre"
        message={`Er du helt sikker på at du vil slette denne ordren?\n\nKunde: ${editingCase?.kunde}\nMaskin: ${editingCase?.maskinMerke}\nBeskrivelse: ${editingCase?.beskrivelse}\n\nDette kan ikke angres!`}
        type="danger"
        confirmText="Slett ordre"
      />
      {/* Bestilling Label Dialog */}
      <BestillingLabelDialog
        isOpen={showBestillingLabelDialog}
        onClose={async () => {
          setShowBestillingLabelDialog(false);
          // Fortsett med lagring uten merkelapp
          setSmsCustomerInfo({ name: formData.kunde, phone: formData.tlf });
          setShowSmsReminder(true);
        }}
        onConfirm={async () => {
          setShowBestillingLabelDialog(false);
          // Skriv ut merkelapp først, deretter lagre
          const caseDataForPrint = { 
            ...formData, 
            id: editingCase ? editingCase.id : Date.now(),
            kategori: "Klar til henting"
          };
          printBestillingLabel(caseDataForPrint);
          
          // Deretter fortsett med normal lagring
          setSmsCustomerInfo({ name: formData.kunde, phone: formData.tlf });
          setShowSmsReminder(true);
        }}const calculateLi
        customerName={formData.kunde}
        items={formData.beskrivelse}
      />

      {/* SMS Reminder Dialog */}
        <SmsReminderDialog
          isOpen={showSmsReminder}
          onClose={() => {
            setShowSmsReminder(false);
            // Lagre ordren etter at SMS-dialogen lukkes
            completeSaveToCategory("Klar til henting");
          }}
          customerName={smsCustomerInfo?.name || ""}
          customerPhone={smsCustomerInfo?.phone || ""}
        />
    </div>
  );
};

export default NyRegistrering;